<template>
  <UIModal
    v-model="isOpen"
    blured
    :close-button="!isProcessing"
    :close-on-click-outside="!isProcessing"
    @closed="emit('closed')"
  >
    <!-- Header -->
    <template #header>
      <h2 class="halloween-expedition-modal__title">{{ $t('HalloweenExpeditionCompletedModalTitle') }}</h2>
    </template>

    <div class="tw:relative tw:gap-4 tw:px-4 tw:sm:px-12 halloween-expedition-modal__content">
      <div class="tw:text-center halloween-expedition-modal__text">
        <p v-if="inventoryItems.length">
          {{ $t('HalloweenExpeditionCompletedModalSubtitle') }}
        </p>
        <p v-else>
          {{ $t('HalloweenExpeditionCompletedModalNoItems') }}
        </p>
      </div>
      <div
        v-if="expeditionState?.seasonPoints && expeditionState.seasonPoints > 0"
        class="halloween-expedition-modal__points__wrapper"
      >
        <div>{{ $t('halloweenExpeditionReceivedSeasonPoints') }}</div>
        <div class="halloween-expedition-modal__points">
          <img :src="tokens.seasonPoints" width="36" height="36" alt="" />
          <div>{{ Formatters.number.renderString((f) => f.formatNumber(expeditionState!.seasonPoints)) }}</div>
        </div>
      </div>

      <div class="tw:pb-36 halloween-expedition-modal__item-cards-row">
        <template v-if="inventoryItems.length">
          <HalloweenItemFrame v-for="item in inventoryItems" :key="item.id" variant="item" :content-gap="0">
            <HalloweenUIItemDisplay
              :height="130"
              :size="72"
              :width="100"
              :item="item"
              :count="item.quantity"
              variant="default"
              direction="vertical"
              :tooltip="false"
              show-name
            />
          </HalloweenItemFrame>
        </template>

        <template v-else>
          <div class="halloween-expedition-modal__empty-wrapper">
            <HalloweenItemFrame v-for="n in 3" :key="n" variant="item" :content-gap="0" class="empty-slot" />
            <div class="halloween-expedition-modal__empty-overlay">
              <HalloweenItemFrame>{{ $t('HalloweenExpeditionCompletedModalEmpySlot') }}</HalloweenItemFrame>
            </div>
          </div>
        </template>
      </div>
      <div
        :class="[
          'tw:bg-[#102b36] tw:bottom-0 tw:left-1/2 tw:-translate-x-1/2 tw:absolute tw:cursor-pointer tw:border tw:border-[#10364a] tw:rounded-lg tw:overflow-y-auto',
          'tw:transition-[max-height] tw:duration-500 tw:ease-in-out',
          isLogExpanded ? 'tw:overflow-y-auto' : 'tw:overflow-hidden'
        ]"
        :style="{ maxHeight: isLogExpanded ? `100%` : '128px', width: 'calc(100% - 48px)' }"
        @click="toggleLogExpanded"
      >
        <div ref="logContentRef" class="tw:flex tw:flex-col tw:gap-2 tw:p-4">
          <div
            v-for="log in logs"
            :key="log.uniqueEventIndex"
            class="tw:flex tw:flex-col tw:pb-2 tw:border-white/20 tw:border-b tw:border-dashed tw:last:border-none"
          >
            <div>
              <div v-for="(text, index) in log.text" :key="text" class="tw:block tw:mb-2">
                <time v-if="index === 0">{{ Formatters.date.toReadableTime(new Date(log.timestampMs)) }}:</time>
                {{ text }}
              </div>
            </div>
            <div class="tw:flex tw:gap-2">
              <div v-for="item in log.items" :key="item.id" class="tw:inline-flex tw:items-center">
                <span :class="{ 'tw:text-[#fb4458]': item.quantity < 0, 'tw:text-[#3fc096]': item.quantity > 0 }">{{
                  Formatters.number.formatDelta(item.quantity)
                }}</span>
                <img
                  :src="halloweenItemRegistry.resolveIcon(item.id)"
                  alt="Expedition Log Image"
                  class="tw:w-10 tw:h-10 tw:object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="halloween-expedition-modal__footer">
        <HalloweenGenericButton :disabled="isProcessing" @click="handleClaimInventory">
          {{ $t('coreContinue') }}
        </HalloweenGenericButton>
      </div>
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import tokens from '~/public/img/tokens';
import type { ExpeditionExtendedState } from '~/types/halloween/expedition';
import { Formatters } from '~/utils/formatters/formatters';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';

const isOpen = defineModel<boolean>({ default: false });

const emit = defineEmits(['closed']);

const halloween = useHalloweenStore();
const location = computed(() => halloween.runtimeSelectedLocation);
const expeditionState = ref<ExpeditionExtendedState | null>(null);

watch(
  () => [halloween.$state.statefulUserExpeditions],
  (value, oldValue, onCleanup) => {
    const oldState = oldValue ? oldValue[0].find((exp) => exp.location === location.value) : null;
    const newState = value[0].find((exp) => exp.location === location.value);

    if (oldState && !newState) expeditionState.value = oldState;
    else expeditionState.value = newState || null;

    onCleanup(() => {
      expeditionState.value = null;
    });
  },
  { immediate: true }
);

const logs = computed(() => expeditionState.value?.logs ?? []);
const isLogExpanded = ref(false);
const logContentRef = ref<HTMLElement | null>(null);
const expandedHeight = ref(500);
const calculateExpandedHeight = () => {
  if (logContentRef.value) {
    expandedHeight.value = logContentRef.value.scrollHeight + 10;
  }
};
const toggleLogExpanded = () => {
  if (!isLogExpanded.value) {
    nextTick(() => {
      calculateExpandedHeight();
      isLogExpanded.value = true;
    });
  } else {
    isLogExpanded.value = false;
  }
};

const inventoryItems = computed(() => expeditionState.value?.inventory ?? []);

// const aliveCharactersCount = computed(() => {
//   if (!expeditionState.value || !expeditionState.value.characters) {
//     return 0;
//   }

//   return expeditionState.value.characters.filter((character) => character.alive === true).length;
// });

const isProcessing = ref(false);
const handleClaimInventory = async () => {
  if (!expeditionState.value) return;
  isProcessing.value = true;

  await halloween
    .claimExpeditionReward(expeditionState.value.expeditionId)
    .then(async (result) => {
      if (result) {
        isOpen.value = false;
        halloween.$state.isFinalModalOpen = true;
        await halloween.updateUserExpeditions();
      }
    })
    .finally(() => {
      isProcessing.value = false;
    });
};
</script>

<style lang="scss" scoped>
.halloween-expedition-modal__points__wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.halloween-expedition-modal {
  &__title {
    font-family: 'PT Serif', serif;
    font-size: 32px;
    font-weight: bold;
    line-height: 30px;
    color: #ffffff;
    text-align: center;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 8px;

    @media (min-width: 640px) {
      padding-left: 48px;
      padding-right: 48px;
    }
  }

  &__content {
    font-family: 'PT Serif', serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 110%;
    color: #dedede;
    max-width: 600px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 100%;
  }

  &__points {
    font-size: 22px;
    color: #eea92e;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__item-cards-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 20px;
    justify-content: center;
  }

  &__empty-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    gap: 20px;
  }

  &__empty-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #ffffff;
    font-weight: 700;
    opacity: 0.6;
    font-size: 24px;
    text-align: center;
    pointer-events: none;
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__footer {
    display: flex;
    justify-content: center;
    gap: 10px;
  }
}

.empty-slot {
  width: 106px;
  height: 123px;

  @media (max-width: 640px) {
    &:nth-child(3) {
      display: none;
    }
  }
}
</style>
